import type { CommonThunk } from 'c-types';
import { callApi, errorMessageFromResponse, normaliseDataThunks } from 'c-data-layer';
import apiClient from 'c-data/apiClient';
import { commonSchemas } from 'c-data';
import { NetworkRequestState } from 'c-data-layer';
import { skinActions } from 'c-admin/Slices/Skin/Skin-slice';
import {
    getUploadingLargeLogoById,
    getUploadingSmallLogoById,
    getUploadingLargeDarkLogoById,
    getUploadingSmallDarkLogoById,
    getAttachingSkinById,
} from './Skin-selectors';

export default {};

export type SkinLogoVariant = 'large' | 'small' | 'largeDark' | 'smallDark';

const logoUploads = {
    large: {
        request: apiClient.Entities.Skin.uploadLargeLogo,
        selector: getUploadingLargeLogoById,
        start: skinActions.startUploadingLargeLogoById,
        finished: skinActions.finishedUploadingLargeLogoById,
        failed: skinActions.failedUploadingLargeLogoById,
    },
    small: {
        request: apiClient.Entities.Skin.uploadSmallLogo,
        selector: getUploadingSmallLogoById,
        start: skinActions.startUploadingSmallLogoById,
        finished: skinActions.finishedUploadingSmallLogoById,
        failed: skinActions.failedUploadingSmallLogoById,
    },
    largeDark: {
        request: apiClient.Entities.Skin.uploadLargeDarkLogo,
        selector: getUploadingLargeDarkLogoById,
        start: skinActions.startUploadingLargeDarkLogoById,
        finished: skinActions.finishedUploadingLargeDarkLogoById,
        failed: skinActions.failedUploadingLargeDarkLogoById,
    },
    smallDark: {
        request: apiClient.Entities.Skin.uploadSmallDarkLogo,
        selector: getUploadingSmallDarkLogoById,
        start: skinActions.startUploadingSmallDarkLogoById,
        finished: skinActions.finishedUploadingSmallDarkLogoById,
        failed: skinActions.failedUploadingSmallDarkLogoById,
    },
};

export const uploadLogo =
    (skinId: number, file: File, variant: SkinLogoVariant): CommonThunk =>
    async (dispatch, getState) => {
        const upload = logoUploads[variant];
        if (upload.selector(getState())(skinId).state === NetworkRequestState.InProgress) {
            return;
        }

        dispatch(upload.start(skinId));
        const formData = new FormData();
        formData.set('file', file);

        const [error, data] = await dispatch(callApi(upload.request({ skinId, formData }).request));

        if (!error) {
            dispatch(normaliseDataThunks.updateWithNormalisedData(commonSchemas.Skin, data.data));
            dispatch(upload.finished(skinId));
            return;
        }
        dispatch(
            upload.failed({
                id: skinId,
                error: errorMessageFromResponse(error),
            }),
        );
    };

export const uploadLargeLogo = (skinId: number, file: File): CommonThunk =>
    uploadLogo(skinId, file, 'large');

export const uploadSmallLogo = (skinId: number, file: File): CommonThunk =>
    uploadLogo(skinId, file, 'small');

export const attachSkin =
    (
        skinId: number,
        options: {
            users?: number[];
            agencies?: number[];
            buyers?: number[];
            clients?: number[];
            owners?: number[];
        },
    ): CommonThunk =>
    async (dispatch, getState) => {
        if (getAttachingSkinById(getState())(skinId).state === NetworkRequestState.InProgress) {
            return;
        }
        dispatch(skinActions.startAttachingSkinById(skinId));

        const [error, data] = await dispatch(
            callApi(apiClient.Entities.Skin.attach({ skinId, ...options }).request),
        );

        if (!error) {
            dispatch(normaliseDataThunks.updateWithNormalisedData(commonSchemas.Skin, data.data));
            dispatch(skinActions.finishedAttachingSkinById(skinId));
            return;
        }
        dispatch(
            skinActions.failedAttachingSkinById({
                id: skinId,
                error: errorMessageFromResponse(error),
            }),
        );
    };
